import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import type { ChatMessage } from "../../@types/agentcore";
import { AivaAvatar } from "./AivaAvatar";

/** Widest a user turn may grow before it wraps, so long turns don't span the panel. */
const USER_BUBBLE_MAX_WIDTH = "379px";

type Media = { type: string; url: string; alt: string };

/** Renders `**bold**` spans and `![alt](image:N)` placeholders as React nodes. */
const formatMessage = (text: string, media?: Media[]): React.ReactNode[] =>
  text.split("\n").flatMap((line, li) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    const combinedRegex = /(\*\*(.+?)\*\*|!\[([^\]]*)\]\(image:(\d+)\))/g;
    let match;
    while ((match = combinedRegex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      if (match[2]) {
        parts.push(<strong key={`${li}-b-${match.index}`}>{match[2]}</strong>);
      } else if (match[4] && media) {
        const idx = parseInt(match[4], 10);
        const item = media[idx];
        if (item) {
          parts.push(
            <img
              key={`${li}-img-${idx}`}
              src={item.url}
              alt={match[3] || item.alt || ""}
              style={{
                maxWidth: "100%",
                borderRadius: "4px",
                display: "block",
                margin: "8px 0",
              }}
            />
          );
        }
      }
      lastIndex = combinedRegex.lastIndex;
    }
    if (lastIndex < line.length) parts.push(line.slice(lastIndex));
    if (li > 0) parts.unshift(<br key={`br-${li}`} />);
    return parts;
  });

/** Reveals `text` word by word. Returns the full text immediately when not animating. */
const useTypewriter = (
  text: string,
  animate: boolean,
  speed = 30,
  onTick?: () => void
): string => {
  const [displayed, setDisplayed] = useState(animate ? "" : text);
  const words = useRef<string[]>([]);
  const idx = useRef(0);

  useEffect(() => {
    if (!animate) {
      setDisplayed(text);
      return;
    }
    words.current = text.split(/(\s+)/);
    idx.current = 0;
    setDisplayed("");
    const timer = setInterval(() => {
      idx.current++;
      setDisplayed(words.current.slice(0, idx.current).join(""));
      onTick?.();
      if (idx.current >= words.current.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, animate, speed]);

  return displayed;
};

const BodyText = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <Text fontSize="T200" color="color.text.primary">
    {children}
  </Text>
);

/**
 * Avatar + name row identifying an Aiva turn. Sits outside the response
 * container so the avatar is never boxed in.
 *
 * The name is Regular weight, not the Medium the mock shows: Stencil treats
 * Medium on a non-interactive label as an accessibility failure, and the design
 * annotations flag it as a defect to correct.
 */
const AivaSender = (): JSX.Element => (
  <Row gridGap="S200" alignItems="center">
    <AivaAvatar />
    <Text fontSize="T100" color="color.text.primary">
      <FormattedMessage id="chatbot-sender-name" />
    </Text>
  </Row>
);

/**
 * An Aiva turn: the sender row, then a single bordered surface holding the
 * response. One surface only, so there is never a card inside a card.
 *
 * `hugContent` shrinks the container to fit its child, used by the thinking
 * indicator where a full-width box around three dots reads as an empty answer.
 */
export const AivaTurn = ({
  children,
  dataTestId,
  hugContent = false,
}: {
  children: React.ReactNode;
  dataTestId?: string;
  hugContent?: boolean;
}): JSX.Element => (
  <Col
    gridGap="S100"
    padding={{ bottom: "S300" }}
    alignItems={hugContent ? "flex-start" : undefined}
    dataTestId={dataTestId}
  >
    <AivaSender />
    <View
      backgroundColor="color.surface.bg-default"
      border="1px solid color.border.primary"
      borderRadius="dimensions.border.radius.200"
      padding="S300"
    >
      {children}
    </View>
  </Col>
);

/**
 * A user turn. Authorship comes from trailing alignment plus the fill colour,
 * so there is deliberately no avatar and no "You" label.
 */
const UserTurn = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <Row justifyContent="flex-end" padding={{ bottom: "S300" }}>
    <View
      backgroundColor="color.fill.muted.blue"
      borderRadius="dimensions.border.radius.200"
      padding={{ top: "S200", bottom: "S200", left: "S300", right: "S300" }}
      maxWidth={USER_BUBBLE_MAX_WIDTH}
    >
      <BodyText>{children}</BodyText>
    </View>
  </Row>
);

/** Welcome or answer text rendered as an Aiva turn. */
export const AivaMessage = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => (
  <AivaTurn>
    <BodyText>{children}</BodyText>
  </AivaTurn>
);

/** The three bouncing dots shown while a reply is pending. */
export const ThinkingIndicator = (): JSX.Element => (
  <AivaTurn dataTestId="chatbotTyping" hugContent>
    <span className="chatbot-thinking-dots">
      <span />
      <span />
      <span />
    </span>
  </AivaTurn>
);

export const MessageBubble = ({
  msg,
  animate,
  onTick,
}: {
  msg: ChatMessage;
  animate: boolean;
  onTick?: () => void;
}): JSX.Element => {
  const displayText = useTypewriter(msg.content, animate, 20, onTick);
  const body = formatMessage(displayText, msg.media);

  return msg.role === "user" ? (
    <UserTurn>{body}</UserTurn>
  ) : (
    <AivaMessage>{body}</AivaMessage>
  );
};
